import React, { useEffect, useState, useRef } from "react";
import AddressFormatter from "../../../app/formatters/AddressFormatter.js";

/** @typedef {import('../models/RpaParkingModel.js').RpaParkingItem} RpaParkingItem */
/** @typedef {import('../models/RpaOptionModel.js').RpaOptionItem} RpaOptionItem */
/** @typedef {import('../services/RpaTransportService.js').ClosestTransportsObjForReservation} ClosestTransportsObjForReservation */

/**
 * @typedef {object} RpaTransportParams
 * @property {Array<RpaOptionItem>} rpaOptions - Liste des options de réservation
 * @property {number} rpaResourceId - Identifiant de la ressource
 * @property {string} rpaReservationStartHour - Heure de début de la réservation
 * @property {string} rpaReservationEndHour - Heure de fin de la réservation
 * @property {function} refreshParking - Fonction pour rafraichir les données de parking
 */

/**
 * @param {RpaTransportParams} props
 */
export default function RpaTransport({ rpaOptions, rpaResourceId, rpaReservationStartHour, rpaReservationEndHour, refreshParking }) {
  const [rpaParkings, setRpaParkings] = useState(/** @type {Array<RpaParkingItem>} */ ([]));
  const [selectedRpaParking, setSelectedRpaParking] = useState(/** @type {?RpaParkingItem} */ (null));
  const [transportObj, setTransportObj] = useState(/** @type {ClosestTransportsObjForReservation} */ ({}));

  useEffect(() => {
    _getRpaParkings();
  }, [rpaResourceId, rpaOptions]);

  useEffect(() => {
    if (selectedRpaParking) {
      _getTransportObj(selectedRpaParking);
    }
  }, [selectedRpaParking]);

  /**
   * Permet de récupérer la liste des stationnements associés à une ressource et de mettre à jour le state
   * @private
   */
  async function _getRpaParkings() {
    var apiUrl = `/api/rpa-transport/parkings/_search?rpaResourceId=${rpaResourceId}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      }),
      data = await response.json();

    setRpaParkings(data);

    setSelectedRpaParking(data.length > 0 ? data[0] : null);
    refreshParking(data.length > 0 ? data[0] : null);
  }

  /**
   * Permet de récupérer les transports associés à une ressource et un stationnement et de mettre à jour le state
   * @param {RpaParkingItem} rpaParking - Stationnement
   * @private
   */
  async function _getTransportObj(rpaParking) {
    var apiUrl = `/api/rpa-transport/_search?rpaParkingId=${rpaParking.id}&rpaResourceId=${rpaResourceId}&rpaReservationStartHour=${rpaReservationStartHour}&rpaReservationEndHour=${rpaReservationEndHour}`,
      response = await fetch(apiUrl, {
        headers: {
          Authorization: "gestiaweb"
        }
      }),
      data = await response.json();

    setTransportObj(data);
  }

  /**
   * Lors du clic sur une carte de stationnement on charge les transports associés
   * @param {RpaParkingItem} rpaParking - Stationnement sélectionné
   * @param {React.MouseEvent} event - Evenement associé au clic sur la carte
   */
  function _onClickOnParkingCard(rpaParking, event) {
    var target = /** @type {HTMLElement} */ (event.target),
      cardNode = /** @type {HTMLElement} */ (target.closest(".card")),
      inputNode = /** @type {HTMLInputElement} */ (cardNode.querySelector("input"));

    setSelectedRpaParking(rpaParking);
    refreshParking(rpaParking);

    inputNode.checked = true;
  }

  return (
    <>
      <div className="grid grid--has-2-child grid--has-2-child-mobile no-mobile">
        {rpaParkings.map((rpaParking, index) => (
          <div className="card" style={{ cursor: "pointer" }} onClick={e => _onClickOnParkingCard(rpaParking, e)} key={index}>
            <div className="card__header">
              <h4>{rpaParking.name}</h4>
            </div>
            <div className="card__description card__description--no-height card__description--center">
              <p>{AddressFormatter.formatAddress(rpaParking.address, rpaParking.postcode, rpaParking.city, rpaParking.complementary_address)}</p>
              <input className="input-control" type="radio" name="rpaParkingId" value={rpaParking.id} aria-label="Stationnement" defaultChecked={rpaParking.id === (selectedRpaParking ? selectedRpaParking.id : null)}></input>
            </div>
          </div>
        ))}
      </div>
      <div>
        {selectedRpaParking ? (
          <>
            {transportObj.closestOutboundTransport ? (
              <>
                <div className="space--16"></div>
                <div className="alert alert--success">
                  <span className="material-symbols-outlined">airport_shuttle</span>
                  <div className="alert__content">
                    <div className="alert__text">
                      Transport aller : {transportObj.closestOutboundTransport.name} - {transportObj.closestOutboundTransport.hour}
                    </div>
                  </div>
                </div>
                <input type="hidden" name="rpaATransportId" value={transportObj.closestOutboundTransport.id} />
              </>
            ) : (
              ""
            )}
            {transportObj.closestReturnTransport ? (
              <>
                <div className="space--16"></div>
                <div className="alert alert--success">
                  <span className="material-symbols-outlined">airport_shuttle</span>
                  <div className="alert__content">
                    <div className="alert__text">
                      Transport retour : {transportObj.closestReturnTransport.name} - {transportObj.closestReturnTransport.hour}
                    </div>
                  </div>
                </div>
                <input type="hidden" name="rpaBTransportId" value={transportObj.closestReturnTransport.id} />
              </>
            ) : (
              ""
            )}
          </>
        ) : (
          ""
        )}
      </div>
    </>
  );
}
